export interface ElevenLabsConfig {
  apiKey: string;
  voiceId?: string;
  model?: string;
  agentId?: string;
}

export interface SpeechOptions {
  text: string;
  voice_id?: string;
  model_id?: string;
  voice_settings?: {
    stability?: number;
    similarity_boost?: number;
  };
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ConversationResponse {
  audioUrl: string;
  transcript: string;
}

export class ElevenLabsService {
  private apiKey: string;
  private baseUrl = 'https://api.elevenlabs.io/v1';
  private defaultVoiceId: string;
  private defaultModel: string;
  private agentId: string;

  constructor(config: ElevenLabsConfig) {
    this.apiKey = config.apiKey;
    this.defaultVoiceId = config.voiceId || 'EXAVITQu4vr4xnSDxMaL'; // Female voice (Bella)
    this.defaultModel = config.model || 'eleven_monolingual_v1';
    this.agentId = config.agentId || 'agent_6301k5dawazefnht8gj9v7c09zn5';
  }

  async generateSpeech(options: SpeechOptions): Promise<Blob> {
    const voiceId = options.voice_id || this.defaultVoiceId;
    const url = `${this.baseUrl}/text-to-speech/${voiceId}`;

    const requestBody = {
      text: options.text,
      model_id: options.model_id || this.defaultModel,
      voice_settings: {
        stability: options.voice_settings?.stability || 0.5,
        similarity_boost: options.voice_settings?.similarity_boost || 0.5,
      },
    };


    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': this.apiKey,
        },
        body: JSON.stringify(requestBody),
      });


      if (!response.ok) {
        const errorText = await response.text();
        
        // Try to parse as JSON for better error handling
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.detail?.message) {
            throw new Error(`Eleven Labs API: ${errorJson.detail.message}`);
          }
        } catch {
          // If not JSON, use original error
        }
        
        throw new Error(`Eleven Labs API error: ${response.status} - ${errorText}`);
      }

      return await response.blob();
    } catch (error) {
      throw error;
    }
  }

  async getVoices() {
    try {
      const response = await fetch(`${this.baseUrl}/voices`, {
        headers: {
          'xi-api-key': this.apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch voices: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  createAudioUrl(audioBlob: Blob): string {
    return URL.createObjectURL(audioBlob);
  }

  revokeAudioUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  // Get signed URL for authenticated WebSocket connection
  async getSignedUrl(): Promise<string> {
    try {
      const url = `${this.baseUrl}/convai/conversation/get-signed-url?agent_id=${this.agentId}`;
      console.log('🔗 DEBUG: Requesting signed URL from:', url);
      console.log('🔗 DEBUG: Using agent ID:', this.agentId);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'xi-api-key': this.apiKey,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ DEBUG: Failed to get signed URL:', response.status, errorText);
        throw new Error(`Failed to get signed URL: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ DEBUG: Received signed URL:', data.signed_url);
      return data.signed_url;
    } catch (error) {
      console.error('❌ DEBUG: Error getting signed URL:', error);
      throw error;
    }
  }

  // Conversational AI WebSocket methods
  createWebSocketConnection(wsUrl: string): WebSocket {
    return new WebSocket(wsUrl);
  }

  // Create a conversation session using WebSocket
  async createConversationSession(agentId?: string, roleProfile?: 'USER' | 'CAREGIVER' | 'HEALTHCARE_PROFESSIONAL' | 'PARTNER' | null): Promise<{
    websocket: WebSocket;
    sendMessage: (text: string) => void;
    close: () => void;
  }> {
    try {
      // Use the provided agentId or fall back to the default one
      const targetAgentId = agentId || this.agentId;
      // For public agents, use the standard ElevenLabs WebSocket endpoint
      const publicWsUrl = `wss://api.elevenlabs.io/v1/convai/conversation?agent_id=${targetAgentId}`;

      // Define intro messages for each role
      const commonIntro = "Hi, I'm Ellie — your Support Coordinator here in The Hub. Think of me as the friendly neighbor who always knows what's happening in town. My job isn't to replace doctors or caregivers, but to make life smoother by keeping the village connected.";

      const introMessages = {
        USER: `${commonIntro} So, this is your village. My job is to help you stay independent and make life easier. I'll walk beside you to organize things, help with health tools, and make sure you feel confident, not overwhelmed. Don't worry — everything here is made to be simple. Big buttons, clear paths, voice options, and help whenever you need it. By the way, if I am speaking too fast, you can hit the pause button to slow me down! What part of The Hub are you most curious about right now?`,

        CAREGIVER: `${commonIntro} I can see you're caring for someone special. That takes heart and strength, and you don't have to do it alone. I'm here to help you coordinate care, track what matters, and connect you with others who understand. Whether it's managing appointments, finding local services, or just having someone to help organize the day-to-day, I've got your back. What would make your life easier right now?`,

        HEALTHCARE_PROFESSIONAL: `${commonIntro} As a healthcare professional, you know coordination is everything. I'm here to help streamline your workflow with The Hub's tools — care summaries, patient engagement tracking, and data that helps you see the full picture. I can show you how to integrate this into your practice so your patients stay connected and supported between visits. What aspect of care coordination interests you most?`,

        PARTNER: `${commonIntro} The Hub philosophy is it takes a village. Our main focus is the collection, analysis and communication of data to those who are supporting themselves or a loved one. At the Hub, we also believe it is important to have others who can support the Hub mission. It sounds like you might be interested in doing so. Tell me what you are interested in learning about or providing to Hub community members.`
      };

      const selectedRole = roleProfile || 'USER';
      const introMessage = introMessages[selectedRole as keyof typeof introMessages];

      console.log('🔗 Connecting to WebSocket, agent:', targetAgentId, 'role:', selectedRole);
      console.log('📤 Intro message:', introMessage);

      return new Promise((resolve, reject) => {
        const websocket = this.createWebSocketConnection(publicWsUrl);

        const sendMessage = (text: string) => {
          console.log('📤 DEBUG: Attempting to send message, WebSocket state:', websocket.readyState);
          if (websocket.readyState === WebSocket.OPEN) {
            // Prepend role_profile if provided
            const messageText = roleProfile
              ? `[RUNTIME]\nrole_profile=${roleProfile}\n\n${text}`
              : text;

            console.log('📤 Sending message to agent:', messageText);

            // Try different message formats that should trigger agent responses
            const messageFormats = [
              // Format 1: user_input (most likely for user messages)
              {
                type: 'user_input',
                text: messageText
              },
              // Format 2: user_message
              {
                type: 'user_message',
                text: messageText
              },
              // Format 3: message with text field
              {
                type: 'message',
                text: messageText
              },
              // Format 4: text_input
              {
                type: 'text_input',
                text: messageText
              }
            ];

            // Try each format
            messageFormats.forEach((msg, index) => {
              setTimeout(() => {
                console.log(`📤 DEBUG: Trying format ${index + 1}:`, JSON.stringify(msg));
                try {
                  websocket.send(JSON.stringify(msg));
                  console.log(`✅ DEBUG: Format ${index + 1} sent successfully`);
                } catch (error) {
                  console.error(`❌ DEBUG: Format ${index + 1} failed:`, error);
                }
              }, index * 500);
            });

          } else {
            console.warn('Cannot send message - WebSocket state:', websocket.readyState);
          }
        };

        const close = () => {
          websocket.close();
        };

        websocket.onopen = () => {
          console.log('✅ WebSocket connected');

          // Send conversation initiation with dynamic variables
          const initMessage = {
            type: 'conversation_initiation_client_data',
            dynamic_variables: {
              intro_message: introMessage
            }
          };
          console.log('📤 Sending initiation with intro_message:', introMessage);
          console.log('📤 Init JSON:', JSON.stringify(initMessage, null, 2));
          websocket.send(JSON.stringify(initMessage));

          // Return the session immediately
          resolve({ websocket, sendMessage, close });
        };

        websocket.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          reject(new Error('Failed to connect to ElevenLabs agent'));
        };

        websocket.onclose = () => {
          // WebSocket disconnected
        };

        // Timeout after 10 seconds
        setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);
      });
    } catch (error) {
      throw new Error(`Failed to create conversation session: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  getAgentId(): string {
    return this.agentId;
  }
}